import { Minus, Plus, Zap } from 'lucide-react';
import { DynamicIcon, type IconName } from 'lucide-react/dynamic';
import React, { useMemo, useState } from 'react';
import { Button, Modal, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { type InventoryItem } from '../../service/api';
import { useInventory } from '../../store/inventory';

interface InventoryActivationModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: InventoryItem;
}

const InventoryActivationModal: React.FC<InventoryActivationModalProps> = ({
  isOpen,
  onClose,
  item,
}) => {
  const [quantity, setQuantity] = useState(1);
  const [isActivating, setIsActivating] = useState(false);
  const { t } = useTranslation();

  const { activateItem } = useInventory();

  const { isMinusEnabled, isPlusEnabled } = useMemo(() => {
    return {
      isMinusEnabled: quantity > 1,
      isPlusEnabled: quantity < item.amount,
    };
  }, [quantity, item.amount]);

  if (!item || !isOpen) return null;

  const { title, description, icon } = item.item;

  const handleQuantityChange = (delta: number) => {
    setQuantity(prev => Math.max(1, Math.min(item.amount, prev + delta)));
  };

  const handleActivate = async () => {
    setIsActivating(true);

    try {
      await activateItem(item._id, quantity);
      onClose();
    } catch (error) {
      console.error('Failed to activate item:', error);
    } finally {
      setIsActivating(false);
    }
  };

  const handleClose = () => {
    if (!isActivating) {
      setQuantity(1);
      onClose();
    }
  };

  return (
    <Modal
      show={isOpen}
      onHide={handleClose}
      centered
      size="lg"
      className="fs-3"
      contentClassName="position-relative"
      backdrop="static"
    >
      <Modal.Dialog
        className="m-0"
        style={{
          backgroundColor: 'rgba(6, 56, 96, 0.95)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
        }}
      >
        <div
          className="position-absolute top-0 start-50 translate-middle d-flex align-items-center justify-content-center rounded-circle bg-primary"
          style={{
            width: '64px',
            height: '64px',
            border: '4px solid rgba(6, 56, 96, 0.95)',
            zIndex: 1,
          }}
        >
          <DynamicIcon
            name={icon as IconName}
            size={32}
            strokeWidth={1.5}
            className="text-white"
          />
        </div>

        <Modal.Header
          closeButton={!isActivating}
          closeVariant="white"
          className="border-0 pt-5 pb-3"
          style={{ backgroundColor: 'transparent' }}
        >
          <Modal.Title className="fs-1 text-white fw-bold w-100 text-center">
            {title}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body
          className="text-white px-4 pb-4"
          style={{ backgroundColor: 'transparent' }}
        >
          {description && (
            <div className="mb-2">
              <h6 className="text-white-50 mb-2">{t('common.description')}</h6>
              <p className="mb-0">{description}</p>
            </div>
          )}

          <div className="mb-4">
            <h6 className="text-white-50 mb-2">
              {t('inventory.available')}: {item.amount}
            </h6>
          </div>

          <div className="mb-5">
            <h6 className="text-white-50 mb-4">{t('shop.quantity')}</h6>
            <div className="d-flex align-items-center justify-content-center gap-3">
              <Button
                variant="outline-light"
                size="lg"
                className="rounded-circle"
                style={{ width: '64px', height: '64px' }}
                onClick={() => handleQuantityChange(-1)}
                disabled={!isMinusEnabled || isActivating}
              >
                <Minus size={24} />
              </Button>

              <div
                className="fs-1 fw-bold text-center"
                style={{ minWidth: '80px' }}
              >
                {quantity}
              </div>

              <Button
                variant="outline-light"
                size="lg"
                className="rounded-circle"
                style={{ width: '64px', height: '64px' }}
                onClick={() => handleQuantityChange(1)}
                disabled={!isPlusEnabled || isActivating}
              >
                <Plus size={24} />
              </Button>
            </div>
          </div>

          <div className="text-center">
            <Button
              variant="success"
              size="lg"
              className="px-5 py-3 fs-3 fw-bold"
              onClick={handleActivate}
              disabled={isActivating}
              style={{ minWidth: '200px' }}
            >
              {isActivating ? (
                <>
                  <Spinner
                    animation="border"
                    size="sm"
                    className="me-2"
                    role="status"
                  />
                  {t('common.loading')}
                </>
              ) : (
                <>
                  <Zap size={28} className="me-2" />
                  {t('inventory.activate')}
                </>
              )}
            </Button>
          </div>
        </Modal.Body>
      </Modal.Dialog>
    </Modal>
  );
};

export default InventoryActivationModal;
export type { InventoryActivationModalProps };
